import { APP_VERSION } from '@/constants/app-version';
import { CUSTOM_ERROR_CODES } from '@/constants/error-codes';
import { authService } from '@/services/auth/auth-service';
import {
  setSession,
  setError as setSessionError,
  setLoading as setSessionLoading,
} from '@/store/auth/slice';
import { ApiErrorRaw } from '@/types/error';
import { APP_ROUTES } from '@/ui/app-routes';
import { getOrCreateClientUuid } from '@/utils/client-uuid';
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query';
import type { BaseQueryApi } from '@reduxjs/toolkit/query';
import camelcaseKeys from 'camelcase-keys';

import { prepareApiHeaders } from './auth-headers';

const BASE_URL = import.meta.env.VITE_PILOTEER_API_BASE_URL;
let isHandlingTerminalAuthError = false;

export const isClientVersionErrorCode = (errorCode?: number | string) =>
  errorCode === CUSTOM_ERROR_CODES.INVALID_CLIENT ||
  errorCode === CUSTOM_ERROR_CODES.UNSUPPORTED_CLIENT;

export const redirectToInvalidClient = () => {
  if (window.location.pathname !== APP_ROUTES.INVALID_CLIENT) {
    window.location.replace(APP_ROUTES.INVALID_CLIENT);
  }
};

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: async (headers, { endpoint }) => {
    // Prefer the live auth session because Redux can lag token refreshes.
    const liveAccessToken = await authService.getAccessToken();
    return prepareApiHeaders(headers, {
      endpoint,
      accessToken: liveAccessToken,
      appVersion: APP_VERSION,
      clientUuid: getOrCreateClientUuid(),
    });
  },
});

const refreshAuthSession = async () => {
  const { data, error } = await authService.refreshSession();

  if (error || !data.session?.accessToken) {
    const sessionResult = await authService.getSession();
    return sessionResult.data.session?.accessToken
      ? sessionResult.data.session
      : null;
  }

  return data.session;
};

let refreshSessionPromise: ReturnType<typeof refreshAuthSession> | null = null;

const isTerminalAuthErrorCode = (errorCode?: number | string) =>
  errorCode === CUSTOM_ERROR_CODES.INVALID_ACCESS_TOKEN ||
  errorCode === CUSTOM_ERROR_CODES.ACCOUNT_DEACTIVATED;

const getFreshAuthSession = async () => {
  if (!refreshSessionPromise) {
    refreshSessionPromise = refreshAuthSession().finally(() => {
      refreshSessionPromise = null;
    });
  }

  return refreshSessionPromise;
};

const forceLogout = async (api: BaseQueryApi) => {
  if (isHandlingTerminalAuthError) return;

  isHandlingTerminalAuthError = true;
  api.dispatch(setSession(null));
  api.dispatch(setSessionLoading(true));
  api.dispatch(setSessionError(null));
  const { error } = await authService.signOut({ scope: 'local' });
  if (error) {
    await authService.clearLocalSession();
  }
  window.location.replace(APP_ROUTES.AUTH.LOGIN);
};

export const baseQueryWithIntercept: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  let apiErrorCode = (result.error?.data as ApiErrorRaw | undefined)
    ?.error_code;

  if (isTerminalAuthErrorCode(apiErrorCode) && !isHandlingTerminalAuthError) {
    if (apiErrorCode === CUSTOM_ERROR_CODES.ACCOUNT_DEACTIVATED) {
      await forceLogout(api);
      return result;
    }

    const refreshedSession = await getFreshAuthSession();

    if (refreshedSession?.accessToken) {
      api.dispatch(setSession(refreshedSession));
      api.dispatch(setSessionLoading(false));
      api.dispatch(setSessionError(null));

      result = await baseQuery(args, api, extraOptions);
      apiErrorCode = (result.error?.data as ApiErrorRaw | undefined)
        ?.error_code;
    }

    if (apiErrorCode === CUSTOM_ERROR_CODES.INVALID_ACCESS_TOKEN) {
      await forceLogout(api);
    }
  }

  if (result.error?.data && isClientVersionErrorCode(apiErrorCode)) {
    redirectToInvalidClient();
  }

  if (result.data) {
    result.data = camelcaseKeys(result.data as Record<string, unknown>, {
      deep: true,
    });
  }

  return result;
};
